import { type SortingState } from '@tanstack/react-table'
import { create } from 'zustand'

import { type ActiveStatusValue } from '@/components/features/products/product-filtering'
import { type CustomerSearchFormData } from '@/lib/validation/loyalty-customer-schemas'
import { type CustomerSearchParams } from '@/models/domain/loyalty-customer'

type SearchType = CustomerSearchFormData['searchType']

interface LoyaltyCustomersState {
  searchParams: CustomerSearchParams
  searchType: SearchType
  searchFormValue: string
}

interface PosUsersState {
  globalFilter: string
  sorting: SortingState
  selectedLevel: string
  selectedLocation: string
  selectedDistrict: string
}

interface UsersState {
  globalFilter: string
  sorting: SortingState
  userFilter: string
}

interface PromotionsState {
  globalFilter: string
  sorting: SortingState
  selectedDba: string
  hidePast: boolean
}

interface SimpleListState {
  globalFilter: string
  sorting: SortingState
}

export type LocationsFilterType = 'all' | 'location' | 'dba' | 'state' | 'company' | 'district'

interface TypeFilteredListState extends SimpleListState {
  selectedType: string
  statusFilter: ActiveStatusValue
}

interface ProductsListState extends TypeFilteredListState {
  /** Products whose units panel is open, as `${type}-${id}` keys. */
  expandedKeys: string[]
}

/** A list whose deactivated rows are hidden until the user asks for them. */
interface DeactivatableListState extends SimpleListState {
  showDeactivated: boolean
}

interface LocationsListState extends SimpleListState {
  filterType: LocationsFilterType
  filterValue: string
}

export type SliceKey =
  | 'loyaltyCustomers'
  | 'loyaltyIncentiveTiers'
  | 'loyaltyRanks'
  | 'posUsers'
  | 'users'
  | 'promotions'
  | 'productTypes'
  | 'priceLevels'
  | 'companies'
  | 'districts'
  | 'locationTypes'
  | 'additionalTaxes'
  | 'dbas'
  | 'locations'
  | 'inventoryAudits'
  | 'productCategories'
  | 'products'
  | 'skus'

interface ListStateStore {
  loyaltyCustomers: LoyaltyCustomersState
  loyaltyIncentiveTiers: DeactivatableListState
  loyaltyRanks: DeactivatableListState
  posUsers: PosUsersState
  users: UsersState
  promotions: PromotionsState
  productTypes: SimpleListState
  priceLevels: SimpleListState
  companies: SimpleListState
  districts: SimpleListState
  locationTypes: SimpleListState
  additionalTaxes: SimpleListState
  dbas: SimpleListState
  locations: LocationsListState
  inventoryAudits: SimpleListState
  productCategories: TypeFilteredListState
  products: ProductsListState
  skus: TypeFilteredListState
  setLoyaltyCustomersState: (patch: Partial<LoyaltyCustomersState>) => void
  setLoyaltyIncentiveTiersState: (patch: Partial<DeactivatableListState>) => void
  setLoyaltyRanksState: (patch: Partial<DeactivatableListState>) => void
  setPosUsersState: (patch: Partial<PosUsersState>) => void
  setUsersState: (patch: Partial<UsersState>) => void
  setPromotionsState: (patch: Partial<PromotionsState>) => void
  setProductTypesState: (patch: Partial<SimpleListState>) => void
  setPriceLevelsState: (patch: Partial<SimpleListState>) => void
  setCompaniesState: (patch: Partial<SimpleListState>) => void
  setDistrictsState: (patch: Partial<SimpleListState>) => void
  setLocationTypesState: (patch: Partial<SimpleListState>) => void
  setAdditionalTaxesState: (patch: Partial<SimpleListState>) => void
  setDBAsState: (patch: Partial<SimpleListState>) => void
  setLocationsState: (patch: Partial<LocationsListState>) => void
  setInventoryAuditsState: (patch: Partial<SimpleListState>) => void
  setProductCategoriesState: (patch: Partial<TypeFilteredListState>) => void
  setProductsState: (patch: Partial<ProductsListState>) => void
  setSkusState: (patch: Partial<TypeFilteredListState>) => void
  resetSlice: (key: SliceKey) => void
  resetAllSlices: () => void
}

export const INITIAL_LOYALTY_CUSTOMERS_STATE: LoyaltyCustomersState = {
  searchParams: {},
  searchType: 'telephone',
  searchFormValue: '',
}

export const INITIAL_LOYALTY_INCENTIVE_TIERS_STATE: DeactivatableListState = {
  globalFilter: '',
  sorting: [{ id: 'min_dollars', desc: false }],
  showDeactivated: false,
}

export const INITIAL_LOYALTY_RANKS_STATE: DeactivatableListState = {
  globalFilter: '',
  sorting: [{ id: 'rank_point_min', desc: false }],
  showDeactivated: false,
}

export const INITIAL_POS_USERS_STATE: PosUsersState = {
  globalFilter: '',
  sorting: [],
  selectedLevel: 'all',
  selectedLocation: 'all',
  selectedDistrict: 'all',
}

export const INITIAL_USERS_STATE: UsersState = {
  globalFilter: '',
  sorting: [{ id: 'user', desc: false }],
  userFilter: 'active',
}

export const INITIAL_PROMOTIONS_STATE: PromotionsState = {
  globalFilter: '',
  sorting: [],
  selectedDba: 'all',
  hidePast: true,
}

export const INITIAL_PRODUCT_TYPES_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [],
}

export const INITIAL_PRICE_LEVELS_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [],
}

export const INITIAL_COMPANIES_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [],
}

export const INITIAL_DISTRICTS_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [{ id: 'name', desc: false }],
}

export const INITIAL_LOCATION_TYPES_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [],
}

export const INITIAL_ADDITIONAL_TAXES_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [],
}

export const INITIAL_DBAS_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [],
}

export const INITIAL_LOCATIONS_STATE: LocationsListState = {
  globalFilter: '',
  sorting: [{ id: 'id', desc: false }],
  filterType: 'all',
  filterValue: '',
}

export const INITIAL_INVENTORY_AUDITS_STATE: SimpleListState = {
  globalFilter: '',
  sorting: [{ id: 'location_id', desc: false }],
}

export const INITIAL_PRODUCT_CATEGORIES_STATE: TypeFilteredListState = {
  globalFilter: '',
  sorting: [{ id: 'name', desc: false }],
  selectedType: 'all',
  statusFilter: 'all',
}

export const INITIAL_PRODUCTS_STATE: ProductsListState = {
  globalFilter: '',
  sorting: [{ id: 'name', desc: false }],
  selectedType: 'all',
  statusFilter: 'all',
  expandedKeys: [],
}

export const INITIAL_SKUS_STATE: TypeFilteredListState = {
  globalFilter: '',
  sorting: [{ id: 'sku', desc: false }],
  selectedType: 'all',
  statusFilter: 'all',
}

const INITIAL_STATES: Record<
  SliceKey,
  | LoyaltyCustomersState
  | DeactivatableListState
  | PosUsersState
  | UsersState
  | PromotionsState
  | SimpleListState
  | LocationsListState
  | TypeFilteredListState
> = {
  loyaltyCustomers: INITIAL_LOYALTY_CUSTOMERS_STATE,
  loyaltyIncentiveTiers: INITIAL_LOYALTY_INCENTIVE_TIERS_STATE,
  loyaltyRanks: INITIAL_LOYALTY_RANKS_STATE,
  posUsers: INITIAL_POS_USERS_STATE,
  users: INITIAL_USERS_STATE,
  promotions: INITIAL_PROMOTIONS_STATE,
  productTypes: INITIAL_PRODUCT_TYPES_STATE,
  priceLevels: INITIAL_PRICE_LEVELS_STATE,
  companies: INITIAL_COMPANIES_STATE,
  districts: INITIAL_DISTRICTS_STATE,
  locationTypes: INITIAL_LOCATION_TYPES_STATE,
  additionalTaxes: INITIAL_ADDITIONAL_TAXES_STATE,
  dbas: INITIAL_DBAS_STATE,
  locations: INITIAL_LOCATIONS_STATE,
  inventoryAudits: INITIAL_INVENTORY_AUDITS_STATE,
  productCategories: INITIAL_PRODUCT_CATEGORIES_STATE,
  products: INITIAL_PRODUCTS_STATE,
  skus: INITIAL_SKUS_STATE,
}

function freshInitialStates() {
  return {
    loyaltyCustomers: { ...INITIAL_LOYALTY_CUSTOMERS_STATE },
    loyaltyIncentiveTiers: { ...INITIAL_LOYALTY_INCENTIVE_TIERS_STATE },
    loyaltyRanks: { ...INITIAL_LOYALTY_RANKS_STATE },
    posUsers: { ...INITIAL_POS_USERS_STATE },
    users: { ...INITIAL_USERS_STATE },
    promotions: { ...INITIAL_PROMOTIONS_STATE },
    productTypes: { ...INITIAL_PRODUCT_TYPES_STATE },
    priceLevels: { ...INITIAL_PRICE_LEVELS_STATE },
    companies: { ...INITIAL_COMPANIES_STATE },
    districts: { ...INITIAL_DISTRICTS_STATE },
    locationTypes: { ...INITIAL_LOCATION_TYPES_STATE },
    additionalTaxes: { ...INITIAL_ADDITIONAL_TAXES_STATE },
    dbas: { ...INITIAL_DBAS_STATE },
    locations: { ...INITIAL_LOCATIONS_STATE },
    inventoryAudits: { ...INITIAL_INVENTORY_AUDITS_STATE },
    productCategories: { ...INITIAL_PRODUCT_CATEGORIES_STATE },
    products: { ...INITIAL_PRODUCTS_STATE },
    skus: { ...INITIAL_SKUS_STATE },
  }
}

export const useListStateStore = create<ListStateStore>((set) => ({
  ...freshInitialStates(),

  setLoyaltyCustomersState: (patch) => {
    set((state) => ({ loyaltyCustomers: { ...state.loyaltyCustomers, ...patch } }))
  },
  setLoyaltyIncentiveTiersState: (patch) => {
    set((state) => ({ loyaltyIncentiveTiers: { ...state.loyaltyIncentiveTiers, ...patch } }))
  },
  setLoyaltyRanksState: (patch) => {
    set((state) => ({ loyaltyRanks: { ...state.loyaltyRanks, ...patch } }))
  },
  setPosUsersState: (patch) => {
    set((state) => ({ posUsers: { ...state.posUsers, ...patch } }))
  },
  setUsersState: (patch) => {
    set((state) => ({ users: { ...state.users, ...patch } }))
  },
  setPromotionsState: (patch) => {
    set((state) => ({ promotions: { ...state.promotions, ...patch } }))
  },
  setProductTypesState: (patch) => {
    set((state) => ({ productTypes: { ...state.productTypes, ...patch } }))
  },
  setPriceLevelsState: (patch) => {
    set((state) => ({ priceLevels: { ...state.priceLevels, ...patch } }))
  },
  setCompaniesState: (patch) => {
    set((state) => ({ companies: { ...state.companies, ...patch } }))
  },
  setDistrictsState: (patch) => {
    set((state) => ({ districts: { ...state.districts, ...patch } }))
  },
  setLocationTypesState: (patch) => {
    set((state) => ({ locationTypes: { ...state.locationTypes, ...patch } }))
  },
  setAdditionalTaxesState: (patch) => {
    set((state) => ({ additionalTaxes: { ...state.additionalTaxes, ...patch } }))
  },
  setDBAsState: (patch) => {
    set((state) => ({ dbas: { ...state.dbas, ...patch } }))
  },
  setLocationsState: (patch) => {
    set((state) => ({ locations: { ...state.locations, ...patch } }))
  },
  setInventoryAuditsState: (patch) => {
    set((state) => ({ inventoryAudits: { ...state.inventoryAudits, ...patch } }))
  },
  setProductCategoriesState: (patch) => {
    set((state) => ({ productCategories: { ...state.productCategories, ...patch } }))
  },
  setProductsState: (patch) => {
    set((state) => ({ products: { ...state.products, ...patch } }))
  },
  setSkusState: (patch) => {
    set((state) => ({ skus: { ...state.skus, ...patch } }))
  },

  resetSlice: (key) => {
    // eslint-disable-next-line security/detect-object-injection
    set({ [key]: { ...INITIAL_STATES[key] } })
  },

  resetAllSlices: () => {
    set(freshInitialStates())
  },
}))
